import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type { User, UserManager } from 'oidc-client-ts'

import { ApiClient, OfflineError, type ClientConfig } from '../data/api'
import { AuthContext, type AuthContextValue, type AuthStatus } from './authContext'
import {
  anmeldeStartFehler,
  CALLBACK_PATH,
  canRenewSilently,
  classifyRenewFailure,
  createUserManager,
  isUsable,
  returnPath,
  SilentRenewUnavailableError,
  singleFlight,
  type SingleFlightSlot,
} from './oidc'

/** Zwischenspeicher der Anmeldeparameter, damit die App offline startet. */
const CONFIG_CACHE_KEY = 'roadbook:client-config'
/** Wohin es nach der Anmeldung zurückgehen soll. */
const RETURN_KEY = 'roadbook:return-to'

/** Im Entwicklungsmodus ohne Anmeldung ersetzt dieser Wert das Token. */
const DEV_TOKEN = 'dev'

/**
 * Die Kennung, unter der im Entwicklungsmodus geschrieben wird.
 *
 * Fest und nicht leer: an dieser Kennung hängt, wem ein Tagebuchbeitrag gehört
 * und wer ihn ändern darf. Ohne sie liesse sich in der Entwicklung kein Beitrag
 * mehr anfassen, den man gerade selbst geschrieben hat.
 */
const DEV_USER_ID = 'dev'

/**
 * Abstand zwischen zwei Erneuerungsversuchen im Offline-Modus.
 *
 * Das online-Ereignis des Fensters ist die schnellere, aber nicht die
 * verlässliche Quelle: hinter einem Captive Portal oder bei schwachem Empfang
 * feuert es gar nicht oder zu früh. Deshalb wird zusätzlich in Ruhe gepollt.
 */
const OFFLINE_RETRY_MS = 60_000

/**
 * Der Authorization Code lässt sich genau einmal einlösen. React ruft Effekte
 * im StrictMode absichtlich doppelt auf, und ein zweiter Tausch scheitert mit
 * invalid_grant. Deshalb wird die laufende Einlösung hier festgehalten, nicht
 * im Effekt: beide Durchläufe warten dann auf dasselbe Ergebnis.
 */
let laufenderRueckweg: Promise<User> | null = null

function loeseCodeEin(manager: UserManager): Promise<User> {
  laufenderRueckweg ??= manager.signinRedirectCallback().catch((err: unknown) => {
    // Nach einem Fehlschlag darf die Sperre nicht liegen bleiben, sonst gibt es
    // für denselben Rückweg keinen zweiten Anlauf mehr.
    laufenderRueckweg = null
    throw err
  })
  return laufenderRueckweg
}

async function loadConfig(): Promise<ClientConfig> {
  const client = new ApiClient(async () => null)
  try {
    const config = await client.fetchConfig()
    localStorage.setItem(CONFIG_CACHE_KEY, JSON.stringify(config))
    return config
  } catch (err) {
    const cached = localStorage.getItem(CONFIG_CACHE_KEY)
    if (cached) return JSON.parse(cached) as ClientConfig
    throw err
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [config, setConfig] = useState<ClientConfig | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [error, setError] = useState<string | null>(null)
  const managerRef = useRef<UserManager | null>(null)

  /**
   * Platz für die eine Erneuerung, die gerade laufen darf.
   *
   * Ohne ihn feuern beim Zurückkommen des Netzes der Retry-Timer der
   * Bibliothek, das online-Ereignis und das Offline-Intervall fast gleichzeitig
   * je ein signinSilent auf demselben, von Authentik rotierten Refresh-Token.
   */
  const erneuerungRef = useRef<SingleFlightSlot<User | null>>({ current: null })

  /**
   * Erneuert das Token still, aber höchstens einmal gleichzeitig.
   *
   * Läuft schon eine Erneuerung, wird deren Ergebnis abgewartet. Alle drei
   * Anlässe - Start, Offline-Wiederkehr und getToken - gehen hier durch.
   */
  const erneuere = useCallback(async (): Promise<User | null> => {
    const manager = managerRef.current
    if (!manager) return null
    return singleFlight(erneuerungRef.current, async () => {
      // Ohne refresh_token nimmt die Bibliothek den iframe-Weg und scheitert an
      // der fehlenden silent_redirect_uri. Das ist terminal, kein Netzproblem.
      const stored = await manager.getUser()
      if (!canRenewSilently(stored)) throw new SilentRenewUnavailableError()
      return manager.signinSilent()
    })
  }, [])

  /**
   * Setzt den Zustand nach einem gescheiterten Versuch - aber erst nach einem
   * letzten Blick in den Speicher.
   *
   * Zwischen dem eigenen Fehlschlag und diesem Moment kann eine parallele
   * Erneuerung gewonnen haben. Dann liegt ein brauchbares Token vor und der
   * Nutzer darf nicht vor die Anmeldemaske gestellt werden.
   *
   * @returns den weiterhin brauchbaren Nutzer, sonst null (dann signed-out).
   */
  const entscheideAbmeldung = useCallback(async (): Promise<User | null> => {
    const manager = managerRef.current
    const aktuell = manager ? await manager.getUser() : null
    if (isUsable(aktuell)) {
      setUser(aktuell)
      setStatus('ready')
      return aktuell
    }
    setStatus('signed-out')
    return null
  }, [])

  useEffect(() => {
    let cancelled = false
    // Was beim Aufräumen wieder abzumelden ist. Der UserManager entsteht erst
    // im asynchronen Teil, der Aufräumpfad muss ihn trotzdem erreichen.
    let manager: UserManager | null = null
    const abmelden: (() => void)[] = []

    void (async () => {
      let loaded: ClientConfig
      try {
        loaded = await loadConfig()
      } catch (err) {
        if (cancelled) return
        setError(
          err instanceof OfflineError
            ? 'Der Server ist nicht erreichbar und es liegen keine gespeicherten Anmeldedaten vor.'
            : 'Die Anmeldeparameter konnten nicht geladen werden.',
        )
        setStatus('error')
        return
      }
      if (cancelled) return
      setConfig(loaded)

      if (loaded.devMode) {
        setStatus('ready')
        return
      }

      manager = createUserManager(loaded)
      managerRef.current = manager
      abmelden.push(manager.events.addUserLoaded(setUser))
      abmelden.push(manager.events.addUserUnloaded(() => setUser(null)))

      // Die bibliothekseigene Erneuerung läuft an dieser App vorbei: sie ruft
      // signinSilent selbst und meldet ihr Scheitern nur über dieses Ereignis.
      // Ohne Handler bliebe der Zustand auf ready stehen, und der Fehlschlag
      // fällt erst beim nächsten getToken auf - unter Umständen erst eine
      // Minute später, wenn der Abgleich das nächste Mal ein Token braucht.
      abmelden.push(
        manager.events.addSilentRenewError((err) => {
          void (async () => {
            if (cancelled) return
            // Dieselbe Einordnung wie überall sonst: nur ein echtes Scheitern
            // der Anmeldung sperrt aus, alles andere ist ein
            // Erreichbarkeitsproblem und führt in den Offline-Modus, aus dem
            // heraus weiter probiert wird.
            if (classifyRenewFailure(err) === 'offline') {
              setStatus('offline')
              return
            }
            console.warn('Stille Erneuerung endgültig gescheitert', err)
            // Nicht blind abmelden: nebenher kann der Einzelflug gerade doch
            // ein brauchbares Token besorgt haben.
            await entscheideAbmeldung()
          })()
        }),
      )

      try {
        if (window.location.pathname === CALLBACK_PATH) {
          const signedIn = await loeseCodeEin(manager)
          const target = sessionStorage.getItem(RETURN_KEY) ?? '/'
          sessionStorage.removeItem(RETURN_KEY)
          window.history.replaceState(null, '', target)
          if (cancelled) return
          setUser(signedIn)
          setStatus('ready')
          return
        }

        const stored = await manager.getUser()
        if (isUsable(stored)) {
          if (cancelled) return
          setUser(stored)
          setStatus('ready')
          return
        }

        if (!stored) {
          if (cancelled) return
          await entscheideAbmeldung()
          return
        }

        // Token abgelaufen. Der Nutzer ist bekannt, nur sein Token nicht mehr
        // gültig: mit Netz lässt es sich erneuern, ohne Netz läuft die App
        // mit dem lokalen Bestand weiter.
        if (cancelled) return
        setUser(stored)
        if (!navigator.onLine) {
          setStatus('offline')
          return
        }
        const renewed = await erneuere()
        if (cancelled) return
        if (isUsable(renewed)) {
          setUser(renewed)
          setStatus('ready')
        } else {
          await entscheideAbmeldung()
        }
      } catch (err) {
        if (cancelled) return
        console.error('Anmeldung fehlgeschlagen', err)

        // Auf dem Rückweg von Authentik gibt es nichts zu retten: ohne
        // eingelösten Code liegt kein Token vor.
        if (window.location.pathname === CALLBACK_PATH) {
          // Die Adresse trägt noch den verbrauchten Code, und den Signin-State
          // dazu hat oidc-client-ts beim ersten Versuch entfernt. Ein Neuladen
          // dieser Adresse scheitert also mit Sicherheit wieder - eine
          // Endlosschleife aus Fehler und Neuladen. Deshalb zurück auf die
          // Startseite, bevor der Fehlerschirm erscheint.
          window.history.replaceState(null, '', '/')
          setError(err instanceof Error ? err.message : 'Die Anmeldung ist fehlgeschlagen.')
          setStatus('error')
          return
        }

        // Sonst gilt dasselbe wie beim späteren Erneuern: nur ein echtes
        // Scheitern der Anmeldung führt zum Anmeldeschirm.
        if (classifyRenewFailure(err) === 'offline') {
          setStatus('offline')
          return
        }
        await entscheideAbmeldung()
      }
    })()

    return () => {
      cancelled = true
      for (const off of abmelden) off()
      // Die stille Erneuerung läuft sonst weiter, auch wenn React den Effekt
      // längst neu gestartet hat. Zwei Erneuerungen auf demselben Refresh-Token
      // enden bei Authentiks Token-Rotation in invalid_grant.
      manager?.stopSilentRenew()
      if (managerRef.current === manager) managerRef.current = null
    }
  }, [entscheideAbmeldung, erneuere])

  // Die bibliothekseigene stille Erneuerung darf nur laufen, solange es etwas
  // zu erneuern gibt. Im Offline-Modus wiederholt ihr Retry-Timer den Versuch
  // sonst alle fünf Sekunden unbegrenzt weiter - am Einzelflug vorbei, denn
  // sie ruft signinSilent selbst - und wetteifert mit dem eigenen Intervall um
  // dasselbe rotierende Refresh-Token.
  useEffect(() => {
    const manager = managerRef.current
    if (!manager) return
    if (status === 'ready') manager.startSilentRenew()
    else manager.stopSilentRenew()
  }, [status])

  // Zurück aus dem Offline-Modus: sobald wieder Netz da ist, wird das Token
  // erneuert. Ohne das bliebe der Abgleich bis zum Neuladen der Seite tot, denn
  // er hängt am Zustand ready.
  useEffect(() => {
    if (status !== 'offline') return
    const manager = managerRef.current
    if (!manager) return

    let cancelled = false

    const versuchen = async () => {
      if (cancelled) return
      try {
        // Mehrfachaufrufe kosten hier nichts: der Einzelflug führt sie
        // zusammen, statt ein zweites Refresh-Token zu verbrennen.
        const renewed = await erneuere()
        if (cancelled) return
        if (isUsable(renewed)) {
          setUser(renewed)
          setStatus('ready')
        }
      } catch (err) {
        if (cancelled) return
        if (classifyRenewFailure(err) === 'signed-out') {
          console.warn('Anmeldung endgültig abgelaufen', err)
          await entscheideAbmeldung()
        }
        // Sonst bleibt es beim Offline-Modus, der nächste Versuch kommt.
      }
    }

    const beiOnline = () => void versuchen()
    window.addEventListener('online', beiOnline)
    const timer = setInterval(() => void versuchen(), OFFLINE_RETRY_MS)

    return () => {
      cancelled = true
      window.removeEventListener('online', beiOnline)
      clearInterval(timer)
    }
  }, [status, erneuere, entscheideAbmeldung])

  const getToken = useCallback(async (): Promise<string | null> => {
    if (config?.devMode) return DEV_TOKEN

    const manager = managerRef.current
    if (!manager) return null

    const current = await manager.getUser()
    if (isUsable(current)) return current.access_token

    // Gar kein Nutzer: hier gibt es nichts zu erneuern und auch keinen lokalen
    // Bestand, der zu jemandem gehört.
    if (!current) {
      const doch = await entscheideAbmeldung()
      return doch?.access_token ?? null
    }

    if (!navigator.onLine) {
      // Ohne Netz ist jeder Versuch aussichtslos. Die App läuft mit den
      // lokalen Daten weiter, der Abgleich ruht.
      setStatus('offline')
      return null
    }

    try {
      const renewed = await erneuere()
      if (isUsable(renewed)) {
        setStatus('ready')
        return renewed.access_token
      }
    } catch (err) {
      console.warn('Token liess sich nicht erneuern', err)
      // navigator.onLine allein taugt nicht als Absicherung: im Funkloch, hinter
      // einem Captive Portal oder bei kurz nicht erreichbarem Anmeldedienst
      // meldet es weiter true. Wer hier pauschal abmeldet, sperrt den Nutzer
      // unterwegs aus seinen eigenen, lokal liegenden Daten aus.
      if (classifyRenewFailure(err) === 'offline') {
        setStatus('offline')
        return null
      }
      const doch = await entscheideAbmeldung()
      return doch?.access_token ?? null
    }

    // Erneuerung ohne Fehler, aber ohne brauchbares Token: dann besteht keine
    // Anmeldung mehr - sofern nicht nebenher doch eine geglückt ist.
    const doch = await entscheideAbmeldung()
    return doch?.access_token ?? null
  }, [config?.devMode, erneuere, entscheideAbmeldung])

  const signIn = useCallback(async () => {
    const manager = managerRef.current
    if (!manager) {
      // Ohne Anmeldeparameter gibt es keinen UserManager. Dann ist genau das
      // Neuladen der richtige Anlauf: es holt die Parameter erneut. Der Knopf
      // auf dem Fehlerschirm bleibt so in jedem Fall wirksam.
      window.location.reload()
      return
    }
    try {
      sessionStorage.setItem(RETURN_KEY, returnPath())
      await manager.signinRedirect()
    } catch (err) {
      // signinRedirect holt vor der Weiterleitung die Metadaten des
      // Anmeldedienstes. Ohne Netz wirft es hier, und gerufen wird ohne await:
      // unbehandelt bliebe davon nur ein Knopf, der scheinbar nichts tut.
      //
      // Der Anmeldezustand bleibt ausdrücklich, wie er war. Sowohl der
      // Anmeldeschirm als auch der Fehlerschirm zeigen die Meldung an und
      // behalten ihren Knopf: sobald das Netz zurück ist, führt derselbe
      // Weg wieder heraus.
      console.warn('Anmeldung liess sich nicht starten', err)
      setError(anmeldeStartFehler(err))
    }
  }, [])

  const signOut = useCallback(async () => {
    const manager = managerRef.current
    // Achtung, Verhaltensänderung: hier wird der lokale Bestand ausdrücklich
    // NICHT mehr gelöscht. signOut spricht nur noch den Anmeldedienst an.
    //
    // Das Löschen gehört der Datenschicht und läuft über wipeForSignOut aus
    // dem Roadbook-Kontext, den dieser Provider nicht erreicht: er liegt im
    // Baum über dem RoadbookProvider. Gerufen wird es deshalb in DataView,
    // gleich vor diesem Aufruf - dort liegen beide Kontexte vor, und nur dort
    // ist auch bekannt, wie viel gerade noch aussteht, was die Rückfrage
    // braucht. Ein wipe() von hier aus hätte die Datenbank unter dem
    // laufenden Abgleich weggeräumt, der daraufhin den Cursor auf 0 liest und
    // den ganzen Bestand des eben abgemeldeten Nutzers zurückholt.
    if (!manager) {
      setStatus('signed-out')
      return
    }
    try {
      await manager.signoutRedirect()
    } catch (err) {
      console.warn('Abmeldung beim Anmeldedienst fehlgeschlagen', err)
      await manager.removeUser()
      setStatus('signed-out')
    }
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      config,
      userId: userIdOf(user, config),
      displayName: displayNameOf(user, config),
      email: emailOf(user, config),
      error,
      getToken,
      signIn,
      signOut,
    }),
    [status, config, user, error, getToken, signIn, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * Die stabile Kennung des Nutzers, der sub-Claim.
 *
 * Sie kommt aus dem gespeicherten Nutzer und nicht aus dem Token: der sub steht
 * im Profil, das oidc-client-ts aus dem ID-Token liest, und der bleibt auch
 * dann lesbar, wenn das Zugriffstoken gerade abgelaufen ist. Genau dann - im
 * Offline-Modus - wird weiter ins Tagebuch geschrieben, und ein Beitrag ohne
 * Verfasser wäre einer, den hinterher niemand mehr ändern darf.
 */
function userIdOf(user: User | null, config: ClientConfig | null): string {
  if (config?.devMode) return DEV_USER_ID
  return user?.profile.sub ?? ''
}

/**
 * Die Adresse aus dem Profil, an der die Mitgliedschaft hängt.
 *
 * Im Entwicklungsmodus ohne Anmeldung gibt es kein Profil; dort entscheidet der
 * Server (DEV_EMAIL in internal/api/middleware.go, überschreibbar mit dem
 * Kopfzeilenfeld X-Dev-Email), und die App holt die Adresse über /api/v1/me.
 */
function emailOf(user: User | null, config: ClientConfig | null): string {
  if (config?.devMode) return ''
  return (user?.profile.email ?? '') as string
}

function displayNameOf(user: User | null, config: ClientConfig | null): string {
  if (config?.devMode) return 'Entwicklung'
  const profile = user?.profile
  if (!profile) return ''
  return (profile.name ?? profile.preferred_username ?? profile.email ?? '') as string
}
